/**
 * @author: chenlv
 * @date: 2025/01/22
 * @description: Genre Trend 品类趋势卡片组件（柱状图/折线图/饼状图）
 */
import React, { useMemo, useState } from 'react';
import classnames from 'classnames';
import NoData from '@intelligence/components/NoData';
import ChartCard from '@intelligence/v3/components/ChartCard';
import { BarChart, LineChart, PieChart } from '@intelligence/v3/components/ChartComponents';
import useIntlExtend from '@intelligence/hooks/useIntlExtend';
import { isEmpty } from '@intelligence/v3/utils/common';
import { getBarOrPieData, getLineData } from './utils';
import {
  createBarChartTooltipFormatter,
  createGenreLineChartTooltipFormatter,
  createPieChartTooltipFormatter,
} from './tooltipFormatters';
import { ChartType } from '@intelligence/v3/enums/chart';
import { METRIC_NAME_I18N_KEY_MAP, PIE_CHART_CENTER_TITLE_CONFIGS } from '../../constants';

import type { IChartCardBaseProps } from './types';

/**
 * GenreTrendCard 组件 Props
 */
interface IGenreTrendCardProps extends IChartCardBaseProps {
  separatedLoading?: boolean;
  /** 日期粒度 */
  granularity?: IGranularity;
  separatedList?: ICasualGenreProfileMetricData[] | null;
}

/**
 * GenreTrendCard 品类趋势卡片组件
 * @description 展示当前选中指标的品类趋势图，支持柱状图/折线图/饼状图切换
 */
const GenreTrendCard: React.FC<IGenreTrendCardProps> = (props) => {
  const {
    style,
    className,
    loading = false,
    separatedLoading = false,
    granularity,
    genreSource,
    genreDimension,
    dataSource,
    metric,
    list,
    separatedList,
    formatGenreName,
    labelFormatOfPieChart,
    xAxisLabelFormatterOfTrendLineChart,
    onPieClick,
    onBarClick,
  } = props;

  const { locale, formatMessage } = useIntlExtend();

  /**
   * 当前激活的图表类型
   */
  const [chartType, setChartType] = useState<ChartType>(ChartType.Line);

  /**
   * 获取卡片标题
   */
  const cardTitle = useMemo(() => {
    const titleKeyMap: Record<IGenreSource, string> = {
      iegg: '',
      sensortower: 'dbi_casual_genre_trend_main_genre',
      annie: '',
      appmagic: 'dbi_casual_genre_trend_super_genre',
    };
    const titleKey = titleKeyMap[genreSource] || '';
    return formatMessage({ id: titleKey });
  }, [genreSource, formatMessage]);

  /**
   * 获取指标名称
   */
  const metricName = useMemo(() => {
    const metricKey = METRIC_NAME_I18N_KEY_MAP[metric] || '';
    return formatMessage({ id: metricKey });
  }, [locale, metric]);

  /**
   * 创建柱状图 tooltip formatter
   */
  const barTooltipFormatter = useMemo(() => {
    return createBarChartTooltipFormatter({
      dataSource,
      metric,
      metricName,
      formatGenreName,
    });
  }, [dataSource, metric, metricName, formatGenreName]);

  /**
   * 创建折线图 tooltip formatter
   */
  const lineTooltipFormatter = useMemo(() => {
    return createGenreLineChartTooltipFormatter({
      dataSource,
      metric,
      metricName,
      formatGenreName,
      granularity,
    });
  }, [dataSource, metric, metricName, formatGenreName, granularity]);

  /**
   * 创建饼状图 tooltip formatter
   */
  const pieTooltipFormatter = useMemo(() => {
    return createPieChartTooltipFormatter({
      dataSource,
      metric,
      metricName,
      formatGenreName,
    });
  }, [dataSource, metric, metricName, formatGenreName]);

  /**
   * 获取趋势图数据
   */
  const lineData = useMemo<ILineChartData[]>(() => {
    if (loading) {
      return [];
    }

    return getLineData({
      genreSource,
      genreDimension,
      dataSource,
      metric,
      list: separatedList,
    });
  }, [loading, genreSource, genreDimension, dataSource, metric, separatedList]);

  /**
   * 获取柱状图、饼状图数据
   */
  const barOrPieData = useMemo(() => {
    if (loading) {
      return [];
    }

    return getBarOrPieData({
      genreSource,
      genreDimension,
      dataSource,
      metric,
      list,
    });
  }, [loading, genreSource, genreDimension, dataSource, metric, list]);

  /**
   * 是否无数据
   */
  const noData = useMemo<boolean>(() => {
    const currentLoading = chartType === ChartType.Line ? separatedLoading : loading;
    if (currentLoading) {
      return false;
    }

    const currentData = chartType === ChartType.Line ? lineData : barOrPieData;
    return isEmpty(currentData);
  }, [loading, separatedLoading, chartType, lineData, barOrPieData]);

  /**
   * 处理图表类型切换
   */
  const handleChartTypeChange = (type: ChartType): void => {
    setChartType(type);
  };

  const isUseBarChartRotate = barOrPieData?.length >= 6;

  /**
   * 渲染对应类型的图表
   */
  const renderChart = (): React.ReactNode => {
    switch (chartType) {
      case ChartType.Bar:
        return (
          <BarChart
            locale={locale as any}
            showLabel={false}
            loading={loading}
            data={barOrPieData}
            useXAxisLabelRotate={isUseBarChartRotate}
            xAxisLabelPadding={isUseBarChartRotate ? [40, 0, 0, 0] : undefined}
            nameFormatter={formatGenreName}
            tooltipFormatter={barTooltipFormatter}
            onBarClick={onBarClick}
          />
        );
      case ChartType.Line:
        return (
          <LineChart
            locale={locale as any}
            loading={loading}
            data={lineData}
            useXAxisLabelRotate={true}
            legendFormatter={formatGenreName}
            tooltipFormatter={lineTooltipFormatter}
            xAxisLabelFormatter={xAxisLabelFormatterOfTrendLineChart}
          />
        );
      case ChartType.Pie:
        return (
          <PieChart
            showLabel
            showLegend
            locale={locale as any}
            loading={loading}
            centerTitle={formatMessage({ id: 'dbi_casual_genre_breakdown' })}
            centerTitleConfigs={PIE_CHART_CENTER_TITLE_CONFIGS}
            data={barOrPieData}
            labelFormatter={labelFormatOfPieChart}
            legendFormatter={formatGenreName}
            tooltipFormatter={pieTooltipFormatter}
            onPieClick={onPieClick}
          />
        );
      default:
        return null;
    }
  };

  return (
    <ChartCard
      style={style}
      className={classnames('dbi-genre-trend-card', className)}
      loading={chartType === ChartType.Line ? separatedLoading : loading}
      title={cardTitle}
      genreSource={genreSource}
      chartTypes={[ChartType.Bar, ChartType.Line, ChartType.Pie]}
      chartType={chartType}
      onChartTypeChange={handleChartTypeChange}
    >
      {noData ? (
        <div className="dbi-genre-trend-card__no-data">
          <NoData content={formatMessage({ id: 'common_nodata' })} />
        </div>
      ) : (
        renderChart()
      )}
    </ChartCard>
  );
};

export default GenreTrendCard;
